import { useHandler } from "@redotech/react-util/hook";
import { Lazy } from "@redotech/react-util/init";
import { InfoModalVersion } from "@redotech/redo-model/team";
import { getEvaluatedInfoModalSettings } from "@redotech/redo-storefront-common/config/info-modal-config";
import { assertNever } from "@redotech/util/type";
import { Suspense, lazy, useContext, useState } from "react";
import { log } from "../monitoring/log";
import { reportTiming } from "../monitoring/timing";
import { addTnplProductToCart } from "./add-product";
import { TryFirstButtonUi } from "./button-ui";
import { TryFirstContext, TryFirstDataSource } from "./context";

const RedoModal = lazy(() =>
  import("../modal").then((mod) => ({ default: mod.RedoModal })),
);

export function TryFirstButton() {
  const context = useContext(TryFirstContext);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const onInfoClick = useHandler(() => {
    setModalOpen(true);
  });

  const onModalClose = useHandler(() => setModalOpen(false));

  if (!context) {
    throw new Error("TryFirstButton must be used within a TryFirstProvider");
  }

  // Only render the button when state is READY
  if (context.state !== "READY") {
    return null;
  }

  reportTiming("tryFirst.buttonRendered");

  const handleTryFirstClick = async (
    event?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { autocheck, sellingPlanId, dataSource } = context;

    setIsLoading(true);

    try {
      switch (dataSource) {
        case TryFirstDataSource.ATTRIBUTES:
          await addTnplProductToCart({
            dataSource,
            autocheck,
            productsToAdd: context.productsToAdd,
            sellingPlanId,
          });
          break;
        case TryFirstDataSource.FORM:
          await addTnplProductToCart({
            dataSource,
            autocheck,
            productForm: context.productForm,
            sellingPlanId,
          });
          break;
        default:
          assertNever(dataSource);
          break;
      }
    } catch (error) {
      console.error("Try First: Error adding to cart", error);
      log("Try First: Failed to add to cart", { error });
    } finally {
      setIsLoading(false);
    }
  };

  const infoModalSettings = getEvaluatedInfoModalSettings({
    settingsTemplate: context.autocheck.tryFirst?.widget?.infoModal || {},
    contractTemplate: context.autocheck.tryFirst?.template,
    useMinutesForTrialPeriod:
      context.autocheck.tryFirst?.useMinutesForTrialPeriod,
  });

  if (globalThis.redoTryFirstBinding?.manualButton) {
    globalThis.redoTryFirstBinding.onTryFirstClick = handleTryFirstClick;
    globalThis.redoTryFirstBinding.onInfoClick = onInfoClick;
  }

  return (
    <>
      {!globalThis.redoTryFirstBinding?.manualButton && (
        <TryFirstButtonUi
          contractTemplate={context.autocheck.tryFirst!.template!}
          customCss={context.autocheck.tryFirst?.widget?.customCss}
          handleTryFirstClick={handleTryFirstClick}
          infoModal={!!infoModalSettings}
          isLoading={isLoading}
          onInfoClick={onInfoClick}
          theme={context.autocheck.tryFirst!.widget!.button!}
        />
      )}
      {infoModalSettings && (
        <Suspense>
          <Lazy init={modalOpen}>
            <RedoModal
              enableTopLayerModalApi={infoModalSettings.enableTopLayerModalApi}
              modalCustomizationsV2={infoModalSettings}
              onClose={onModalClose}
              open={modalOpen}
              packageProtectionEnabled={false}
              packageProtectionPlusEnabled={false}
              returnsCoverage={undefined}
              version={infoModalSettings.version || InfoModalVersion.V2}
            />
          </Lazy>
        </Suspense>
      )}
    </>
  );
}
